/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import queryString from 'querystring';

import { Endpoints, Infer } from 'shared';

import { store } from '../store';
import { authRefreshUser } from '../store/auth/actions';
import { ApiClient } from '../types/api/ApiClient';
import { EntityId } from '../types/api/EntityId';
import { CreateOrganizationRequest } from '../types/organization/CreateOrganizationRequest';
import { OidcConfiguration } from '../types/organization/OidcConfiguration';
import { Organization } from '../types/organization/Organization';
import { UpdateOrganizationRequest } from '../types/organization/UpdateOrganizationRequest';
import { getApiEndpoint } from '../utils/getApiEndpoint';
import { apiClientFactory } from '../utils/http-client/apiClientFactory';
import { axiosInstanceFactory } from '../utils/http-client/axiosInstanceFactory';
import { axiosJwtTokenInterceptor, axiosUnauthorizedInterceptor } from '../utils/http-client/axiosInterceptors';

class OrganizationApiServiceClass {
  private api: ApiClient;

  constructor() {
    this.api = apiClientFactory(
      axiosInstanceFactory(
        { baseURL: getApiEndpoint() },
        [axiosJwtTokenInterceptor],
        [[undefined, axiosUnauthorizedInterceptor]],
      ),
    );
  }

  fetchAllOrganizations = () => {
    return this.api.get<Organization[]>('/organization');
  };

  createOrganization = (data: CreateOrganizationRequest) => {
    return this.api.post<Organization>('/organization', data).then(data => {
      store.dispatch(authRefreshUser());
      return data;
    });
  };

  inviteUserToOrganization = (organizationId: EntityId, data: { email: string }) => {
    return this.api.post(`/organization/${organizationId}/user`, data);
  };

  fetchOrganizationById = ({ organizationId }: { organizationId: EntityId }) => {
    return this.api.get<Infer<typeof Endpoints.GetOrganization>['Response'][200]>(
      Endpoints.GetOrganization.route({ organizationId }),
    );
  };

  deleteOrganizationById = ({ organizationId }: { organizationId: EntityId }) => {
    return this.api.del(`/organization/${organizationId}`);
  };

  fetchDashboardForOrganization = ({ organizationId }: { organizationId: EntityId }) => {
    return this.api.get<Infer<typeof Endpoints.GetOrganizationDashboard>['Response'][200]>(
      Endpoints.GetOrganizationDashboard.route({ organizationId }),
    );
  };

  updateOrganizationById = (id: EntityId, data: Partial<UpdateOrganizationRequest>) => {
    return this.api.patch<Organization>(`/organization/${id}`, data);
  };

  updateOrganizationThumbnail = (
    id: EntityId,
    body: { type: 'seed'; seed: string } | { type: 'base64'; base64: string },
  ) => {
    return this.api.put(`/organization/${id}/thumbnail`, body);
  };

  getOrganizationThumbnailUrl = ({ organizationId, v = 1 }: { organizationId: EntityId; v?: string | number }) => {
    return `${getApiEndpoint()}/organization/${organizationId}/thumbnail?${queryString.encode({
      v,
    })}`;
  };

  deleteOrganizationThumbnail = (organizationId: EntityId) => {
    return this.api.del(`/organization/${organizationId}/thumbnail`);
  };

  getOidcConfiguration = (organizationId: string) => {
    return this.api.get<OidcConfiguration>(Endpoints.GetOidcConfiguration.route({ organizationId }));
  };
  setOidcConfiguration = (organizationId: string, body: Infer<typeof Endpoints.PutOidcConfiguration>['Body']) => {
    return this.api.put(Endpoints.PutOidcConfiguration.route({ organizationId }), body);
  };
}

export const OrganizationApiService = new OrganizationApiServiceClass();
